// CMS slot registry — Wave C.
//
// Pure TS, no DB, no runtime deps. Every named editable slot on marketing
// pages is declared here with:
//   • key       — stable identifier (also persisted in cms_slots.key)
//   • page      — closed PageId union for tab grouping in admin
//   • kind      — discriminator for body shape (markdown / lists)
//   • label     — admin UI display
//   • helpText  — admin UI hint
//   • default   — baked fallback, preserves typography on cold load
//
// Consumers:
//   • The SPA's `useCmsSlot(key)` looks up the worker-injected blob first;
//     on miss falls back to the registry default.
//   • The worker uses this registry to know which slots belong to which page
//     for the per-page KV cache.
//   • Admin UI iterates the registry to render forms.
//
// Slot keys are append-only — to retire a slot, mark it `deprecated: true`
// rather than removing the entry; otherwise existing override rows orphan.

import { EXTRA_SLOTS } from './slots-extra';

export type PageId =
  | 'home'
  | 'faq'
  | 'how_it_works'
  | 'founding_venue_offer'
  | 'what_we_are_and_arent'
  | 'about'
  | 'dispute_resolution'
  | 'privacy'
  | 'terms'
  | 'community_guidelines'
  | 'notes_from_the_road'
  | 'waitlist'
  | 'next_steps'
  | 'footer';

// Tab labels track the live public page names: How It Works, Our Ethos
// (=what_we_are_and_arent), Founding Venues, Ripples (=notes_from_the_road),
// Our Story (=about). Non-nav pages keep descriptive labels. See PAGE_IDS for tab order.
//
// The label is the only thing that moves when a page is renamed. `PageId`s are
// append-only and are keyed on by every override row in `cms_slots`, so
// `what_we_are_and_arent` stays `what_we_are_and_arent` even though nothing
// calls the page that any more — it is now "Our Ethos" (Richie, 2026-08-18).
export const PAGE_LABELS: Record<PageId, string> = {
  home: 'Homepage',
  how_it_works: 'How It Works',
  what_we_are_and_arent: 'Our Ethos',
  founding_venue_offer: 'Founding Venues',
  notes_from_the_road: 'Ripples',
  about: 'Our Story',
  faq: 'FAQ',
  waitlist: 'Waitlist capture',
  dispute_resolution: 'Dispute resolution',
  community_guidelines: 'Community guidelines',
  privacy: 'Privacy',
  terms: 'Terms',
  next_steps: 'Read on cards',
  footer: 'Site footer',
};

export type SlotKind = 'markdown' | 'bullet_list' | 'qa_list' | 'step_list' | 'image' | 'image_list';

export type QaItem = {
  id: string;
  chip?: string;
  question: string;
  answer: string[];
};

export type StepItem = {
  title: string;
  body: string;
  footnote?: string;
};

/**
 * An admin-managed marketing image. `url` is a same-origin path the SPA renders
 * directly (a baked static default like `/img/hero/x.avif`, or an uploaded /
 * AI-Studio-assigned `/img/img/<hash>.ext`). `key` is the R2 key when the image
 * was uploaded or assigned (absent for static defaults). `width`/`height` are
 * best-effort intrinsic dimensions, omitted when not parseable (AVIF/WebP).
 *
 * Layout (opacity, overlay wash, object-position, z-index) stays owned by the
 * consuming component — the slot only carries the photo + its alt text.
 */
export type ImageBody = {
  key?: string;
  url: string;
  alt: string;
  width?: number;
  height?: number;
  /**
   * Render opacity (0–1) for the photo as a background layer. Omitted falls
   * back to the consuming component's baked default. The overlay wash / object
   * position / z-index stay component-owned; only opacity is admin-tunable.
   */
  opacity?: number;
  /**
   * Optional short caption shown over the image. Used by the "Who's on wwaave"
   * marquee tiles (an artist × venue pairing, e.g. "A DJ in a distillery") so
   * the team types the label alongside each tile rather than baking it into the
   * artwork. Background `image` slots ignore it. Capped at
   * {@link MAX_IMAGE_CAPTION_CHARS} on the write path.
   */
  caption?: string;
};

/**
 * An ordered gallery of admin-managed images — the body shape for an
 * `image_list` slot. Used by the homepage "Who's on wwaave" marquee, where each
 * row is one list of up to {@link MAX_IMAGE_LIST_ITEMS} tiles the team can add,
 * remove, reorder, and swap independently. Each entry is a plain {@link ImageBody}
 * (same validation as a single-image slot); layout stays component-owned.
 */
export type ImageListBody = ImageBody[];

export type SlotBodyByKind = {
  markdown: string;
  bullet_list: string[];
  qa_list: QaItem[];
  step_list: StepItem[];
  image: ImageBody;
  image_list: ImageListBody;
};

export type SlotBody = SlotBodyByKind[SlotKind];

export type SlotDef<K extends SlotKind = SlotKind> = {
  key: string;
  page: PageId;
  kind: K;
  label: string;
  helpText?: string;
  default: SlotBodyByKind[K];
  /** Append-only retirement: keep the row, hide from the admin UI. */
  deprecated?: boolean;
};

// ──────────────────────────────────────────────────────────────────────────────
// Registry
// ──────────────────────────────────────────────────────────────────────────────

const FAQ_DEFAULTS: QaItem[] = [
  {
    id: 'is-it-free-for-artists',
    chip: 'Cost',
    question: 'Is it free for artists?',
    answer: [
      'Yes. Artists never pay to use WWAAVE. Venues pay a subscription; we don’t take a cut of any payment between you and a host.',
    ],
  },
  {
    id: 'what-is-an-exchange',
    chip: 'Vocab',
    question: 'What’s an "exchange"?',
    answer: [
      'An exchange is a single visit. An artist comes to a venue to perform, and in return the venue offers a place to stay. Some exchanges include a stipend; many don’t. Either side can decline.',
    ],
  },
  {
    id: 'is-money-on-platform',
    chip: 'Payments',
    question: 'Does WWAAVE process payments?',
    answer: [
      'No. Any money that changes hands between an artist and a venue (a stipend, a contribution to travel) happens off-platform. WWAAVE is a marketplace, not a payment rail.',
    ],
  },
  {
    id: 'safety',
    chip: 'Safety',
    question: 'How do you handle safety concerns?',
    answer: [
      'We verify hosts and artists, hold a real dispute-resolution process, and remove anyone who breaks the promise. Read the dispute-resolution page for the full process.',
    ],
  },
  {
    id: 'where-are-you-live',
    chip: 'Coverage',
    question: 'Where is WWAAVE live?',
    answer: [
      'We’re onboarding our first 50 venues across south-east Queensland (Brisbane, Sunshine Coast, Northern Rivers). The roadmap expands corridor by corridor, Australia first.',
    ],
  },
];

const ARTIST_STEPS_DEFAULTS: StepItem[] = [
  {
    title: 'Sign up and verify',
    body: 'A photo, a city, and a paragraph about what you would bring to a room. Verification takes two minutes and unlocks the verified badge.',
  },
  {
    title: 'Browse venues',
    body: 'Filter by city, art form, capacity, sleeping arrangement, vibe. Map view shows what is along your route.',
  },
  {
    title: 'Send a request',
    body: 'Write a real letter. Your dates, your sound, your party size. The host reads it and replies, usually within a day.',
  },
  {
    title: 'Confirm and travel',
    body: 'Once the host confirms, the address is shared. You and the host coordinate arrival in the thread.',
  },
  {
    title: 'Stay, play, leave a review',
    body: 'After the exchange completes, both sides write a review. They go live together, yours and theirs at the same moment.',
  },
];

const HOST_STEPS_DEFAULTS: StepItem[] = [
  {
    title: 'Join the founding cohort',
    body: 'The first 50 venues get hand-onboarding, a permanent founding badge, a free first year, and the $19 AUD/month rate locked for life.',
  },
  {
    title: 'Build your profile',
    body: 'Photos of the space, a description of the room, art forms you welcome, what you offer in exchange.',
  },
  {
    title: 'Verify',
    body: 'Two minutes. Government ID plus a photo of you with it. We review within 72 hours.',
  },
  {
    title: 'Receive requests',
    body: 'Artists send letters with their dates and what they would bring. You read them at your pace.',
  },
  {
    title: 'Confirm or decline',
    body: 'Confirm with one tap. Decline with a kind reason. We share the reason with the artist if you want us to.',
  },
  {
    title: 'Host and review',
    body: 'After the stay, write a review of the artist. Yours and theirs go live together.',
  },
];

const OFFER_LINES_DEFAULTS: string[] = [
  'One year free. No credit card up front.',
  'Founding rate locked for life. It won’t go up.',
  'Permanent Founding Venue mark on your profile.',
  'Hand-onboarding from the WWAAVE team.',
];

// ──────────────────────────────────────────────────────────────────────────────
// Homepage step defaults
// ──────────────────────────────────────────────────────────────────────────────

const HOME_HOW_STEPS_DEFAULTS: StepItem[] = [
  {
    title: 'A venue posts',
    body: 'A host puts up what they have. A room, a stage, a spare bed, a recording rig. They say what they want in return.',
  },
  {
    title: 'An artist finds',
    body: 'A touring muso browses, finds a stay that fits the route, and sends a request. No agents, no booking fees in the middle.',
  },
  {
    title: 'WWAAVE makes the intro',
    body: 'Both sides confirm and the conversation opens. From there it is two people sorting a gig, the way it should be.',
  },
];

const HOME_PROMISES_DEFAULTS: StepItem[] = [
  {
    title: "We'll never hold your money.",
    body: 'There is no escrow because there is no cash changing hands. The exchange is the stay and the set, traded straight.',
  },
  {
    title: "We'll never take a cut.",
    body: 'No cut of your sweat or your sound. A host venue pays one flat membership, an artist can start for free, and that is the whole of it.',
  },
  {
    title: "We'll never sell your data.",
    body: 'Your details are yours. We are not in the business of packaging you up for anyone, and we never will be.',
  },
];

const HOME_POSTSCRIPT_TILES_DEFAULTS: StepItem[] = [
  {
    title: 'How it works',
    body: 'Browse, write a request, stay, leave a review. The flow in detail.',
  },
  {
    title: 'The WWAAVE Promise',
    body: 'No cuts, no premium-placement paywalls, no data sales. What WWAAVE is and isn’t.',
  },
  {
    title: 'How we hold the line',
    body: 'Community standards and how we keep the network honest.',
  },
  {
    title: 'Founding venues',
    body: 'Spots are filling. A free first year, then the founding rate ($19 AUD/month, locked for life), closes once 50 venues join.',
  },
  {
    title: 'Run by a muso, not a tech bro',
    body: "Richie's story, from a worm farm in Bulgaria to the platform he wished existed. From a small ripple, wwaaves are made.",
  },
  {
    title: 'Notes from the road',
    body: 'Quarterly letters from Richie about what’s working and what isn’t.',
  },
  {
    title: 'FAQ',
    body: 'Five questions we hear most: free?, verification, disputes, reviews, where we’re live.',
  },
];

// ──────────────────────────────────────────────────────────────────────────────
// About page defaults
// ──────────────────────────────────────────────────────────────────────────────

const ABOUT_ORIGIN_PARAS: string[] = [
  'I found music young, inside a community that’s still connected through creativity to this day. Not as a spectator, but as part of something. A bunch of people who built nights out of nothing, who showed up for each other, who knew a good time by the vibe rather than the lineup. That community held me through some wild years, and shaped everything that followed. The DJing, the hosting, running events, the years of walking into spaces and figuring out how to make something happen.',
  'Music wasn’t a life choice. It was something I couldn’t live without. It still is. And the thing I picked up earliest, the thing that turned out to be the whole point, is that creativity is how people find each other. Years later, the same faces are still in my life, and many more too, connected by what we make. That’s not nostalgia. It’s the seed of everything WWAAVE is.',
  'The other thing that got its hooks into me early was movement. Travelling the way musos travel. On a shoestring, solo, with a partner, with friends, sometimes with a crowd. Wherever I went, I was looking for the same thing I’d found as a young lad: the right space, the right people, the feeling of belonging somewhere unexpected.',
  'By my side through most of it, tolerating, supporting, and occasionally questioning every one of my impulses, is Nina. My partner and travel soul mate, the person who has watched me walk into more beach bars and ask more strangers if I can play tunes than any human should reasonably have to witness.',
  'Here’s what I kept running into, everywhere. The same moment, over and over. You’d land somewhere. A beach town in Thailand, a listening bar in Laos, a basement in Beijing. And you’d get the feeling that you and this place might be good for each other. The vibe was right. The people were right. There was a chance something could happen. But making it real meant cold messages, unanswered emails, awkward introductions at venue doors, and a lot of faith that something would eventually stick. Most of the time, you just showed up and hoped.',
];

const ABOUT_PROMISE_CARDS: StepItem[] = [
  {
    title: 'Five percent of net profits to disadvantaged artists.',
    body: 'Once the platform is profitable, five percent of net profits flow each year to programmes supporting artists from disadvantaged backgrounds: first-generation touring acts, regional artists, artists from communities underrepresented in the live circuit. Public-facing. Non-negotiable.',
  },
  {
    title: 'Ten percent of venue subscription revenue to the referring artist.',
    body: "If an artist brought a venue onto the platform, ten percent of that venue's monthly subscription revenue is paid back to the artist for as long as the venue stays subscribed. Artists are the reason venues join. They share the upside.",
  },
  {
    title: 'Free access for artists. Always.',
    body: 'Artists never pay to sign up, never pay to send a request, never pay to be listed, never pay to be verified. The monetisation model is venue subscriptions. That is the only line that funds the platform. It will not move.',
  },
];

// ──────────────────────────────────────────────────────────────────────────────
// What-we-are-and-arent defaults
// ──────────────────────────────────────────────────────────────────────────────

const WHAT_WE_ARE_TAGLINES: string[] = [
  'WWOOFing for creatives.',
  'Couchsurfing for artists.',
  'A living community.',
];

const WHAT_WE_ARE_IS_LIST: string[] = [
  'Warm: a place where the first message is a real letter, not a templated form.',
  'Mutual: an artist offers a performance; a venue offers a stay, a meal, an audience.',
  'Verified: every artist and host can show that they are who they say they are.',
  'Non-cash: stipends, travel costs, and meals are between the parties, never on the platform.',
  'Trust-led: reviews, dispute resolution, and verification are visible on every profile.',
];

const WHAT_WE_ARE_ISNT_LIST: string[] = [
  'A discovery platform for fans.',
  'A ticket marketplace.',
  'A streaming alternative.',
  'A pay-to-pitch booking service.',
  'A generic hospitality platform.',
  'A way to upcharge artists for "premium" features.',
];

// ──────────────────────────────────────────────────────────────────────────────
// Dispute resolution defaults
// ──────────────────────────────────────────────────────────────────────────────

const DISPUTE_CATEGORIES: StepItem[] = [
  { title: 'Safety.', body: 'Anything that put you at risk during a stay.' },
  {
    title: 'Misrepresentation.',
    body: 'The space, the audience, or the arrangement was materially different from what was described.',
  },
  {
    title: 'Cancellation.',
    body: 'A cancellation that was abrupt, late, or without reason.',
  },
  {
    title: 'Harassment.',
    body: "Conduct that crossed a line. Yours, the other party's, or anyone else's in the room.",
  },
  {
    title: 'Cash dispute.',
    body: 'A disagreement about a stipend or travel contribution that was discussed before the exchange.',
  },
  { title: 'Other.', body: 'If none of the above fits, tell us in the description.' },
];

const DISPUTE_PROCESS_STEPS: StepItem[] = [
  {
    title: 'You raise the concern.',
    body: 'From the thread, tap "Raise a concern." Pick a category, write at least 80 characters about what happened, attach evidence if you have it.',
  },
  {
    title: 'We acknowledge within 48 hours.',
    body: 'Our trust & safety team reads it, marks the exchange as "under review," and tells both parties we’ve received it.',
  },
  {
    title: 'We hear both sides.',
    body: 'A separate dispute thread opens with each party. The original exchange thread stays read-only while we investigate.',
  },
  {
    title: 'We decide and tell you.',
    body: 'Both parties get a written summary of what we found and what happens next. Outcomes range from a clarifying note through to suspension of either account.',
  },
  {
    title: 'We close the exchange.',
    body: 'The exchange moves to its final state. Reviews left up to that point may or may not be released, depending on the outcome.',
  },
];

const DISPUTE_PRINCIPLES: string[] = [
  'We read every concern. There is no algorithmic triage.',
  "We don't take sides until we've heard both.",
  "We don't share evidence files between parties without consent, only the written summary.",
  'Frivolous concerns can lead to account suspension. Real ones are taken seriously, every time.',
  "Cash transactions between parties are out of scope. WWAAVE doesn't handle money. We can mediate the conversation but we can't enforce a refund.",
];

// ──────────────────────────────────────────────────────────────────────────────
// Privacy + Terms defaults
// ──────────────────────────────────────────────────────────────────────────────

const PRIVACY_WHAT_WE_COLLECT: StepItem[] = [
  {
    title: 'Account information',
    body: 'email address, display name, password (hashed, so we never see the plaintext).',
  },
  {
    title: 'Profile information',
    body: 'the photos, descriptions, and links you choose to publish.',
  },
  {
    title: 'Verification evidence (hosts)',
    body: "to verify a host's identity we ask for a government-issued ID and a photo of you holding it. We check it, then delete it. We don't keep the document, and we never record the ID number. Once the check is done the record holds only the fact that you were verified, by whom, and when. We never show the document to other users, only the verified badge.",
  },
  {
    title: 'Verification evidence (artists)',
    body: "artists aren't asked for ID at all. An artist is verified automatically once they've confirmed their mobile number by SMS, added a profile photo, and linked at least one public profile of their own. No document is uploaded, so there's nothing for us to hold.",
  },
  {
    title: 'Exchange and message content',
    body: 'the letters and replies you send through the platform.',
  },
  {
    title: 'Operational data',
    body: 'sign-in events, IP address (hashed for audit), device info.',
  },
];

const PRIVACY_WHAT_WE_DONT_DO: string[] = [
  "We don't sell your contact details.",
  "We don't share verification documents with other users, only the verified badge.",
  "We don't use third-party tracking pixels.",
  "We don't profile users for advertising. We don't serve advertising at all.",
];

const TERMS_COVENANT_ITEMS: string[] = [
  'Exchanges between artists and venues are non-cash. Stipends and travel costs are between the parties.',
  "WWAAVE doesn't take a cut of any payment between you and the other party.",
  'You will treat the other party with respect, follow through on confirmed exchanges, and communicate honestly.',
  'You will not use WWAAVE to harass, defraud, or solicit anyone.',
];

// ──────────────────────────────────────────────────────────────────────────────
// What-we-are-and-aren't section defaults
// ──────────────────────────────────────────────────────────────────────────────

const WHAT_WE_ARE_PROMISE_NEVER: string[] = [
  'Take a cut of any payment between an artist and a venue.',
  'Charge artists a fee to send a request, or to be listed.',
  'Sell your contact details to a label, an agent, or a venue group.',
  'Gate trust signals like verification, reviews and dispute resolution behind a paywall.',
  'Mediate cash transactions between parties.',
];

const WHAT_WE_ARE_MODEL: string[] = [
  'Venues subscribe to be discoverable. The founding-venue rate is free for the first year, capped at the first 50 venues and locked for life. After that, the standard plan is $19 AUD/month from day one.',
  'That is the whole model. No cut of the night, no fees on the exchange, no data sales, no paid placement.',
];

const WHAT_WE_ARE_COMMITMENT: string[] = [
  "5% of net profits go to disadvantaged artists: subsidised access, funded residencies, and support for those who can't afford to travel or create.",
  '10% of every venue subscription is paid back to the artist who referred them, rewarding the community that grew it.',
  "Free artist access, always. Creative travel shouldn't be limited by budget.",
];

// ──────────────────────────────────────────────────────────────────────────────
// Community guidelines defaults
// ──────────────────────────────────────────────────────────────────────────────

const CG_EXPECTATIONS: StepItem[] = [
  {
    title: 'Warmth.',
    body: 'Treat the first message like a letter, not a form. A short hello, a real reason for the request, a sense of who you are. Both directions.',
  },
  {
    title: 'Honesty.',
    body: 'Describe the space, the audience, the set, the gear, the stay as they actually are. The platform reads better when the post is accurate than when it oversells.',
  },
  {
    title: 'No surprises.',
    body: 'Anything that affects the exchange is written down in the thread before the date: load-in, sound check, who feeds whom, where the artist sleeps, what a stipend covers. The thread is the record.',
  },
  {
    title: 'Follow through.',
    body: 'If you confirm, you show up. If something changes, you say so early. Late cancellations and silent no-shows hurt the network more than a careful no.',
  },
  {
    title: 'Hosts to each other.',
    body: 'Artists are not employees and venues are not customers. Both sides are hosts in their own way. One offers a stage, the other offers a performance. The promise runs both directions.',
  },
];

const CG_REFUSALS: StepItem[] = [
  {
    title: 'Exploitation in either direction.',
    body: 'Using the platform to extract free labour from artists, or to extract a free place to stay from venues, without honouring the rest of the exchange.',
  },
  {
    title: 'Cash-only "gigs" disguised as exchanges.',
    body: 'WWAAVE is an exchange: a performance for a stay, a meal, an audience. Posting a paid gig and calling it an exchange to dodge a subscription is not what this is.',
  },
  {
    title: 'Bait-and-switch on the stay.',
    body: 'Showing a private room and substituting a couch on arrival. Promising breakfast and providing nothing. What a host posts is the contract.',
  },
  {
    title: 'Coercive reviews.',
    body: 'Pressuring the other party for a particular review, threatening retaliation for an honest one, or offering anything in exchange for a star rating. Reviews are double-blind for a reason.',
  },
  {
    title: 'Taking down a profile to dodge a dispute.',
    body: "Deleting an artist profile or a venue page mid-dispute does not pause the process. We resolve what's in front of us either way, and the outcome lands on whatever account is restored later.",
  },
  {
    title: 'Harassment or discrimination.',
    body: 'Of any party, on any axis: gender, race, sexuality, faith, disability, age. There is no second warning on this one.',
  },
  {
    title: 'Using off-platform payment to apply pressure.',
    body: 'Cash between parties is between parties. Holding payment outside the platform over someone to skip verification, reviews, or the dispute process is treated as bad-faith use of the network.',
  },
];

const CG_GUARDRAILS: StepItem[] = [
  {
    title: 'Verified profiles.',
    body: 'Every artist and host can show that they are who they say they are. The trust signals on a profile are not behind a paywall. They are the point of the platform.',
  },
  {
    title: 'Double-blind reviews.',
    body: "Both parties write before either reads. Reviews release together once the second is in, or together at the window's close. Neither side can pre-empt the other.",
  },
  {
    title: 'Dispute resolution, read by a human.',
    body: 'No algorithmic triage. A real person reads every concern, hears both sides, and writes a summary. The flow is documented end-to-end.',
  },
  {
    title: 'The Exchange Certificate.',
    body: 'Every completed exchange leaves a permanent record on both profiles: a verifiable credential of trust that travels with the artist and the venue.',
  },
  {
    title: 'Human admin on every escalation.',
    body: 'When a decision affects an account, whether verification, suspension or removal, a human signs off. Audit-logged. Reviewable.',
  },
];

// ──────────────────────────────────────────────────────────────────────────────
// About page extended defaults
// ──────────────────────────────────────────────────────────────────────────────

const ABOUT_LUANG_PRABANG: string[] = [
  'I remember arriving in Luang Prabang. Relief first, that exhale of finding a sweet spot to drop out for a while after weeks of moving. But underneath it, that fizzing feeling. We were meeting two best friends there, and I felt the whole experience would be more fulfilling if we could play music together.',
  "We found it. A hip Buddha bar lounge run by a Canadian, the kind of place that felt like it had been waiting for exactly this. We got chatting, asked if we could DJ the next evening, said we'd play in exchange for a meal, some drinks and a room. He was open. And it felt like we'd connected on a deeper level straight away, like old friends, all of us buzzing to play together the next day. That was the exchange, working exactly as it should. No money. No middleman. Just two sides who had what the other wanted, and the luck of having found each other.",
  "I still have the notes I wrote on a bus through Kazakhstan, playing with the words and the meaning of this thing. We'd just left Bulgaria, where I'd ended up headlining a Balkan music festival off the back of a WWOOF stay on a worm farm, and as we pointed ourselves toward South East Asia, it crystallised. I could land anywhere, if only a platform existed that opened the way in advance.",
  "WWOOFing had already cracked it open for me. Find a host, make contact, show up, work the land, get fed, housed, welcomed, folded into a community. No cash, no transaction. Just an honest exchange between people who need what the other has. It worked on farms. Why couldn't it work for creatives? That question didn't go away. It followed me for over a decade.",
];

const ABOUT_WHY_EXISTS: string[] = [
  'Then I finally pulled out those travel notes and started building the answer. WWAAVE, the Worldwide Artist and Venue Exchange, is the platform I wished had existed every time I arrived somewhere new. A way for a travelling creative to build a profile, reach out to the right spaces before showing up, and land in a trusted community ready to trade hospitality for a performance, a set, a mural, a workshop, whatever they bring.',
  'No cash on the platform. No booking fees. No middleman taking a cut of the night. Just a fair exchange between people who have something to offer each other.',
  "It started as an idea on the road in 2012. It became real all these years later. And it still runs on the same belief that got its hooks into me as a young DJ. That an awesome venue and an amazing artist can create magic if it's easy to find each other.",
];

const BASE_SLOTS: ReadonlyArray<SlotDef> = [
  // ─── Homepage ─────────────────────────────────────────────────────────
  {
    key: 'home.step_init.headline',
    page: 'home',
    kind: 'markdown',
    label: 'Step 1: Headline',
    default: 'Worldwide Artist and Venue Exchange.',
  },
  {
    key: 'home.step_init.body',
    page: 'home',
    kind: 'markdown',
    label: 'Step 1: Hero sub-line',
    default: 'Bring what you create, get a place to stay. No cash, no fees, no middleman.',
  },
  // Retired in the Brand Pack v3 homepage restructure — the StepVenue /
  // StepArtist sections were folded into the StepHowItWorks card row, which is
  // not (yet) CMS-wired. Kept as deprecated rows so any existing override
  // doesn't orphan; hidden from the admin editor via getSlotDefsForPage.
  {
    key: 'home.step_venue.headline',
    page: 'home',
    kind: 'markdown',
    label: 'Step 2: Venue headline',
    default: 'A venue posts the night they want to fill.',
    deprecated: true,
  },
  {
    key: 'home.step_venue.body',
    page: 'home',
    kind: 'markdown',
    label: 'Step 2: Venue body',
    default:
      'They have a room, a stage, and a kind audience. What they do not have is a booking fee.',
    deprecated: true,
  },
  {
    key: 'home.step_artist.headline',
    page: 'home',
    kind: 'markdown',
    label: 'Step 3: Artist headline',
    default: 'An artist finds it on their way through.',
    deprecated: true,
  },
  {
    key: 'home.step_artist.body',
    page: 'home',
    kind: 'markdown',
    label: 'Step 3: Artist body',
    default:
      'They need a place to stay and a room to play. They send a request, the host replies, and the exchange begins.',
    deprecated: true,
  },
  {
    key: 'home.step_logic.headline',
    page: 'home',
    kind: 'markdown',
    label: 'Step 4: Promise headline',
    default: "Three things we'll never do.",
  },
  {
    key: 'home.step_logic.lede',
    page: 'home',
    kind: 'markdown',
    label: 'Step 4: Promise lede',
    default:
      'The platform is built around a few hard lines. They are not a marketing promise, they are how the thing works.',
  },
  {
    key: 'home.step_exit.headline',
    page: 'home',
    kind: 'markdown',
    label: 'Step 6: Exit headline',
    default: 'Bring what you create, get a place to stay.',
  },
  {
    key: 'home.step_exit.subtitle',
    page: 'home',
    kind: 'markdown',
    label: 'Step 6: Exit subtitle',
    default: 'Two halves of one exchange. Tell us which one is you.',
  },
  {
    key: 'home.step_init.hero_image',
    page: 'home',
    kind: 'image',
    label: 'Step 1: Hero background photo',
    helpText:
      'Full-bleed background behind the opening headline. Sits under a cream wash; use the opacity slider to set how strongly it shows. Leave alt blank — it is decorative. Revert restores the original WWAAVE photo.',
    default: { url: '/img/hero/hero-musician.avif', alt: '', opacity: 0.38 },
  },
  {
    key: 'home.step_how.background_image',
    page: 'home',
    kind: 'image',
    label: 'Step 2: "How it works" background photo',
    helpText:
      'Ambient background behind the three-step section. Use the opacity slider to set how strongly it shows. Leave alt blank — it is decorative. Revert restores the original WWAAVE photo.',
    default: { url: '/img/hero/story-welcome.avif', alt: '', opacity: 0.18 },
  },
  {
    key: 'home.step_exit.background_image',
    page: 'home',
    kind: 'image',
    label: 'Step 6: Closing background photo',
    helpText:
      'Ambient background behind the closing sign-up split. Use the opacity slider to set how strongly it shows. Leave alt blank — it is decorative. Revert restores the original WWAAVE photo.',
    default: { url: '/img/hero/story-afterglow.avif', alt: '', opacity: 0.26 },
  },
  // Step 2 — "How it works" (the three-step section).
  {
    key: 'home.step_how.eyebrow',
    page: 'home',
    kind: 'markdown',
    label: 'Step 2: Eyebrow',
    default: 'How it works',
  },
  {
    key: 'home.step_how.headline',
    page: 'home',
    kind: 'markdown',
    label: 'Step 2: Headline',
    default: 'Three steps, no cash, no middleman.',
  },
  {
    key: 'home.step_how.intro',
    page: 'home',
    kind: 'markdown',
    label: 'Step 2: Intro paragraph',
    default:
      'A host venue offers a stay. A touring artist offers a set. WWAAVE puts the two in the same room and then gets out of the way.',
  },
  {
    key: 'home.step_how.steps',
    page: 'home',
    kind: 'step_list',
    label: 'Step 2: The three steps',
    helpText:
      'Each entry is one step card (title + body). The step number and icon are fixed by position — the first three entries map to the place / music / mail icons. Keep it to three for the row layout.',
    default: HOME_HOW_STEPS_DEFAULTS,
  },
  // Step 3 — The Smart Rider. Retired from the homepage 2026-08-10: on the
  // homepage the rider had no context around it, so it read as abstract
  // (Richie, feedback DYFqwfvei8). It now sits on the How It Works page, after
  // the two flows that explain what a confirmed exchange even is — see the
  // how_it_works.rider_* slots. Kept as deprecated rows (append-only) so the
  // existing override rows don't orphan; a migration copies their bodies across.
  {
    key: 'home.step_rider.eyebrow',
    page: 'home',
    kind: 'markdown',
    label: 'Step 3: Eyebrow (retired)',
    helpText: 'Retired — the Smart Rider moved to the How It Works page.',
    default: 'The one thing we generate',
    deprecated: true,
  },
  {
    key: 'home.step_rider.headline',
    page: 'home',
    kind: 'markdown',
    label: 'Step 3: Headline (retired)',
    helpText: 'Retired — the Smart Rider moved to the How It Works page.',
    default: 'The Smart Rider.',
    deprecated: true,
  },
  {
    key: 'home.step_rider.intro',
    page: 'home',
    kind: 'markdown',
    label: 'Step 3: Intro paragraph (retired)',
    helpText: 'Retired — the Smart Rider moved to the How It Works page.',
    default:
      'Built from fields you and the host already filled in, no second form. Shared in the thread the moment the exchange is confirmed, and re-built whenever a detail changes.',
    deprecated: true,
  },
  // Step 4 — The WWAAVE Promise (headline + lede already above).
  {
    key: 'home.step_logic.eyebrow',
    page: 'home',
    kind: 'markdown',
    label: 'Step 4: Eyebrow',
    default: 'The WWAAVE Promise',
  },
  {
    key: 'home.step_logic.promises',
    page: 'home',
    kind: 'step_list',
    label: 'Step 4: The three promises',
    helpText:
      'Each entry is one promise card: title is the promise (e.g. "We’ll never take a cut."), body is the reasoning. The "01 / NN" counter tracks the number of entries automatically.',
    default: HOME_PROMISES_DEFAULTS,
  },
  // Step 5 — The founding offer + waitlist (live "X of 50" stat stays dynamic).
  {
    key: 'home.step_founding.headline',
    page: 'home',
    kind: 'markdown',
    label: 'Step 5: Offer headline',
    default: 'Become a founding venue.',
  },
  {
    key: 'home.step_founding.body',
    page: 'home',
    kind: 'markdown',
    label: 'Step 5: Offer body',
    helpText: 'The "X of 50 claimed" counter and scarcity line are live and not editable here.',
    default:
      'The standard plan is $19 AUD a month. The first 50 host venues join as founders: free for the first year, then the founding rate locked for life. When the 50 are gone, the offer closes.',
  },
  {
    // Retired 2026-07-10: the homepage "not ready yet" waitlist card was removed
    // (feedback N33) — all homepage CTAs now route to sign-up. Slot kept (append-only).
    key: 'home.step_founding.waitlist_headline',
    page: 'home',
    kind: 'markdown',
    label: 'Step 5: Waitlist headline',
    default: 'Join the waitlist.',
    deprecated: true,
  },
  {
    key: 'home.step_founding.waitlist_body',
    page: 'home',
    kind: 'markdown',
    label: 'Step 5: Waitlist body',
    default:
      'Leave an email and we’ll let you know as we open up new regions and slots.',
    deprecated: true,
  },
  // Persistent sign-up pill (floats once you scroll past the hero).
  {
    key: 'home.sticky_cta.text',
    page: 'home',
    kind: 'markdown',
    label: 'Sticky sign-up pill text',
    default: 'Ready to join?',
  },
  // Closing "More about WWAAVE" link grid.
  {
    key: 'home.postscript.eyebrow',
    page: 'home',
    kind: 'markdown',
    label: 'Postscript: Eyebrow',
    default: 'Read on',
  },
  {
    key: 'home.postscript.headline',
    page: 'home',
    kind: 'markdown',
    label: 'Postscript: Headline',
    default: 'More about WWAAVE.',
  },
  {
    key: 'home.postscript.lede',
    page: 'home',
    kind: 'markdown',
    label: 'Postscript: Lede',
    default: 'The substance behind the story. Pages you can browse, share, and come back to.',
    // "More about us" subheading removed site-wide at Richie's request — no
    // longer rendered. Retired (not deleted) so any override doesn't orphan;
    // drops out of the admin CMS editor.
    deprecated: true,
  },
  {
    key: 'home.postscript.tiles',
    page: 'home',
    kind: 'step_list',
    label: 'Postscript: Link cards',
    helpText:
      'Each entry is a link card (title + description). The destination link of each card is fixed by position and set in code — editing here changes the wording only, not where a card points. Order matches: How it works, The WWAAVE Promise, How we hold the line, Founding venues, About, Notes from the road, FAQ.',
    default: HOME_POSTSCRIPT_TILES_DEFAULTS,
    // Retired: MarketingPostscript now renders per-tile title/description slots
    // (home.postscript.tile_*_{title,body}) mapped statically to fixed hrefs, so
    // copy can't drift onto the wrong link. Kept as a deprecated row so any
    // existing override doesn't orphan; hidden from the admin editor.
    deprecated: true,
  },

  // ─── About ────────────────────────────────────────────────────────────
  {
    key: 'about.hero_lede',
    page: 'about',
    kind: 'markdown',
    label: 'Hero lede',
    default:
      'WWAAVE is the platform I spent fifteen years wishing existed: land anywhere, and turn what you create into a place to stay. No cash, no fees, no middleman.',
  },
  {
    key: 'about.origin_story',
    page: 'about',
    kind: 'bullet_list',
    label: 'Origin story paragraphs',
    helpText: 'One paragraph per entry. They render stacked with breathing room.',
    default: ABOUT_ORIGIN_PARAS,
  },
  {
    // Retired 2026-07-10: the Promise section was removed from the Our Story
    // page (feedback CbRz). The promise still lives on the homepage (StepLogic)
    // and /what-we-are-and-arent via their own slots. Slot kept (append-only).
    key: 'about.promise_cards',
    page: 'about',
    kind: 'step_list',
    label: 'Community promises',
    helpText:
      "Three commitments rendered as cards. Each one's title is bold, body is the explanation.",
    default: ABOUT_PROMISE_CARDS,
    deprecated: true,
  },

  // ─── What we are and aren't ───────────────────────────────────────────
  {
    // Retired 2026-07-03: the "In a sentence / What we're building" taglines
    // card was removed from the page during the Promise-page reorder. Slot kept
    // (append-only) but hidden from the CMS + no longer rendered.
    key: 'what_we_are_and_arent.taglines',
    page: 'what_we_are_and_arent',
    kind: 'bullet_list',
    label: 'Taglines (one per line)',
    default: WHAT_WE_ARE_TAGLINES,
    deprecated: true,
  },
  // Retired 2026-08-18: the whole "In contrast" section (the two tick/cross
  // columns) was removed from the page — Richie had typed "REMOVE THIS SECTION"
  // into its eyebrow and title (feedback y5uYAuKIbZM4aNSh323Lx). Slots kept
  // (append-only) but hidden from the CMS + no longer rendered; prod holds real
  // overrides on both lists.
  {
    key: 'what_we_are_and_arent.is_list',
    page: 'what_we_are_and_arent',
    kind: 'bullet_list',
    label: 'What WWAAVE IS, bullet points',
    default: WHAT_WE_ARE_IS_LIST,
    deprecated: true,
  },
  {
    key: 'what_we_are_and_arent.isnt_list',
    page: 'what_we_are_and_arent',
    kind: 'bullet_list',
    label: "What WWAAVE ISN'T, bullet points",
    default: WHAT_WE_ARE_ISNT_LIST,
    deprecated: true,
  },

  // ─── Dispute resolution ───────────────────────────────────────────────
  {
    key: 'dispute_resolution.categories',
    page: 'dispute_resolution',
    kind: 'step_list',
    label: 'Concern categories',
    helpText: "Each entry's title is bold (e.g. 'Safety.'), body is the description.",
    default: DISPUTE_CATEGORIES,
  },
  {
    key: 'dispute_resolution.process_steps',
    page: 'dispute_resolution',
    kind: 'step_list',
    label: 'Five-step process',
    default: DISPUTE_PROCESS_STEPS,
  },
  {
    key: 'dispute_resolution.principles',
    page: 'dispute_resolution',
    kind: 'bullet_list',
    label: 'Principles bullet list',
    default: DISPUTE_PRINCIPLES,
  },

  // ─── Privacy ──────────────────────────────────────────────────────────
  {
    key: 'privacy.what_we_collect',
    page: 'privacy',
    kind: 'step_list',
    label: 'What we collect',
    helpText: 'Title = category (e.g. "Account information"), body = description.',
    default: PRIVACY_WHAT_WE_COLLECT,
  },
  {
    key: 'privacy.what_we_dont_do',
    page: 'privacy',
    kind: 'bullet_list',
    label: "What we don't do",
    default: PRIVACY_WHAT_WE_DONT_DO,
  },

  // ─── Terms ────────────────────────────────────────────────────────────
  {
    key: 'terms.covenant_items',
    page: 'terms',
    kind: 'bullet_list',
    label: 'Exchange Covenant bullet items',
    default: TERMS_COVENANT_ITEMS,
  },

  // ─── FAQ ──────────────────────────────────────────────────────────────
  {
    key: 'faq.items',
    page: 'faq',
    kind: 'qa_list',
    label: 'FAQ items',
    helpText: 'Question + multi-paragraph answer. Each chip surfaces above the question.',
    default: FAQ_DEFAULTS,
  },
  // ─── How it works ─────────────────────────────────────────────────────
  {
    key: 'how_it_works.artist_steps',
    page: 'how_it_works',
    kind: 'step_list',
    label: 'Artist flow',
    default: ARTIST_STEPS_DEFAULTS,
  },
  {
    key: 'how_it_works.host_steps',
    page: 'how_it_works',
    kind: 'step_list',
    label: 'Host flow',
    default: HOST_STEPS_DEFAULTS,
  },
  {
    key: 'how_it_works.artist_eyebrow',
    page: 'how_it_works',
    kind: 'markdown',
    label: 'Artist flow: eyebrow',
    default: 'For artists',
  },
  {
    key: 'how_it_works.artist_title',
    page: 'how_it_works',
    kind: 'markdown',
    label: 'Artist flow: title',
    default: 'From sign-up to your first review.',
  },
  {
    key: 'how_it_works.artist_lede',
    page: 'how_it_works',
    kind: 'markdown',
    label: 'Artist flow: lede',
    default: 'Five steps. Each one takes minutes; the only long part is the road between them.',
    // Retired 2026-07-06 (Richie: "REMOVE THIS LINE"). The artist-flow lede no
    // longer renders; deprecated so it drops out of the CMS admin.
    deprecated: true,
  },
  {
    key: 'how_it_works.host_eyebrow',
    page: 'how_it_works',
    kind: 'markdown',
    label: 'Host flow: eyebrow',
    default: 'For hosts',
  },
  {
    key: 'how_it_works.host_title',
    page: 'how_it_works',
    kind: 'markdown',
    label: 'Host flow: title',
    default: 'From sign-up to a returning roster.',
  },
  {
    key: 'how_it_works.host_lede',
    page: 'how_it_works',
    kind: 'markdown',
    label: 'Host flow: lede',
    default:
      'Six steps. Hand-onboarded for the first 50 venues. Slower, kinder, and we help with the photos.',
    // Retired 2026-07-06 (Richie: "REMOVE THIS LINE"). The host-flow lede no
    // longer renders; deprecated so it drops out of the CMS admin.
    deprecated: true,
  },
  {
    key: 'how_it_works.fine_print_eyebrow',
    page: 'how_it_works',
    kind: 'markdown',
    label: 'Fine print: eyebrow',
    default: 'The fine print',
  },
  {
    key: 'how_it_works.fine_print_title',
    page: 'how_it_works',
    kind: 'markdown',
    label: 'Fine print: title',
    default: 'A few things worth knowing.',
  },
  {
    key: 'how_it_works.fine_print_items',
    page: 'how_it_works',
    kind: 'bullet_list',
    label: 'Fine print: bullets (one per line)',
    default: [
      "WWAAVE doesn't handle money between artists and venues. Stipends, travel contributions, food, those are between you. We hold the introduction and the trust scaffolding around it.",
      'The address of a venue is shared once the exchange is confirmed, not before.',
      "Reviews are mutual. Neither side reads the other's review until both are submitted. After 14 days, whichever review is in goes live alone.",
      'Either party can raise a concern from any thread. Our trust & safety team responds within 48 hours.',
    ],
  },
  // ─── Founding venue offer ─────────────────────────────────────────────
  {
    key: 'founding_venue_offer.headline',
    page: 'founding_venue_offer',
    kind: 'markdown',
    label: 'Hero headline',
    default: 'Founding venues.',
  },
  {
    key: 'founding_venue_offer.lede',
    page: 'founding_venue_offer',
    kind: 'markdown',
    label: 'Hero lede',
    default:
      "The first 50 venues to join WWAAVE shape what the platform becomes. We want hosts we'd send any artist to without thinking twice.",
  },
  {
    key: 'founding_venue_offer.offer_lines',
    page: 'founding_venue_offer',
    kind: 'bullet_list',
    label: 'Offer bullet points',
    helpText: 'One bullet per line. Keep each line punchy. These are scanned, not read.',
    default: OFFER_LINES_DEFAULTS,
    // Retired: superseded by the founding_venue_offer.offer_items step_list, where
    // each item carries its own headline + supporting sentence (no cross-array
    // index zip, so pairing survives a reorder). Kept as a deprecated row so any
    // existing override doesn't orphan; hidden from the admin editor.
    deprecated: true,
  },
  {
    key: 'founding_venue_offer.hero_image',
    page: 'founding_venue_offer',
    kind: 'image',
    label: 'Hero background photo',
    helpText:
      'Background photo behind the hero headline. Use the opacity slider to set how strongly it shows (higher = bolder photo, thinner cream wash). Keep it moderate so the headline stays readable. Leave alt blank — it is decorative. Revert restores the original WWAAVE photo.',
    default: { url: '/img/hero/hero-energetic.avif', alt: '', opacity: 0.4 },
  },
  // ─── Footer ───────────────────────────────────────────────────────────
  {
    key: 'footer.tagline',
    page: 'footer',
    kind: 'markdown',
    label: 'Tagline',
    default: 'A marketplace where touring artists and host venues meet.',
  },
  {
    key: 'footer.legal_entity',
    page: 'footer',
    kind: 'markdown',
    label: 'Legal entity line',
    helpText: 'Shows under "Legal" in the footer.',
    default: 'WWAAVE PTY LTD · ACN 689 639 423',
  },
  {
    key: 'footer.contact_email',
    page: 'footer',
    kind: 'markdown',
    label: 'Contact email',
    default: 'hello@wwaave.com',
  },

  // ─── Content-page hero photos ─────────────────────────────────────────
  // Each marketing page's MarketingHero gets an admin-settable background
  // photo. Defaults are subtle (low opacity → thick cream wash) so the hero
  // type stays readable; raise the opacity to make the photo bolder, or set it
  // to 0 to fall back to a near-plain wash.
  {
    key: 'about.hero_image',
    page: 'about',
    kind: 'image',
    label: 'Hero background photo',
    helpText:
      'Background photo behind the About hero. Higher opacity = bolder photo, thinner wash. Keep it subtle so the headline stays readable.',
    default: { url: '/img/hero/story-welcome.avif', alt: '', opacity: 0.2 },
  },
  {
    key: 'how_it_works.hero_image',
    page: 'how_it_works',
    kind: 'image',
    label: 'Hero background photo',
    helpText:
      'Background photo behind the How it works hero. Higher opacity = bolder photo, thinner wash.',
    default: { url: '/img/hero/hero-musician.avif', alt: '', opacity: 0.2 },
  },
  {
    key: 'faq.hero_image',
    page: 'faq',
    kind: 'image',
    label: 'Hero background photo',
    helpText: 'Background photo behind the FAQ hero. Higher opacity = bolder photo, thinner wash.',
    default: { url: '/img/hero/story-welcome.avif', alt: '', opacity: 0.18 },
  },
  {
    key: 'what_we_are_and_arent.hero_image',
    page: 'what_we_are_and_arent',
    kind: 'image',
    label: 'Hero background photo',
    helpText:
      'Background photo behind the "What we are and aren’t" hero. Higher opacity = bolder photo, thinner wash.',
    default: { url: '/img/hero/story-afterglow.avif', alt: '', opacity: 0.2 },
  },
  {
    key: 'dispute_resolution.hero_image',
    page: 'dispute_resolution',
    kind: 'image',
    label: 'Hero background photo',
    helpText:
      'Background photo behind the Dispute resolution hero. Kept subtle for a calm, readable page.',
    default: { url: '/img/hero/story-afterglow.avif', alt: '', opacity: 0.14 },
  },
  {
    key: 'privacy.hero_image',
    page: 'privacy',
    kind: 'image',
    label: 'Hero background photo',
    helpText: 'Background photo behind the Privacy hero. Kept subtle for a readable legal page.',
    default: { url: '/img/hero/story-welcome.avif', alt: '', opacity: 0.12 },
  },
  {
    key: 'terms.hero_image',
    page: 'terms',
    kind: 'image',
    label: 'Hero background photo',
    helpText: 'Background photo behind the Terms hero. Kept subtle for a readable legal page.',
    default: { url: '/img/hero/story-welcome.avif', alt: '', opacity: 0.12 },
  },
  {
    key: 'community_guidelines.hero_image',
    page: 'community_guidelines',
    kind: 'image',
    label: 'Hero background photo',
    helpText:
      'Background photo behind the Community guidelines hero. Higher opacity = bolder photo, thinner wash.',
    default: { url: '/img/hero/story-welcome.avif', alt: '', opacity: 0.18 },
  },
  {
    key: 'notes_from_the_road.hero_image',
    page: 'notes_from_the_road',
    kind: 'image',
    label: 'Hero background photo',
    helpText:
      'Background photo behind the Notes from the road hero (the index and each letter). Higher opacity = bolder photo, thinner wash.',
    default: { url: '/img/hero/story-afterglow.avif', alt: '', opacity: 0.18 },
  },

  // ─── Marketing hero copy ──────────────────────────────────────────────
  // Each content page's MarketingHero eyebrow / title / lede. (Founding venue
  // offer already has its headline + lede slots above; its eyebrow is the live
  // cohort counter and stays dynamic.)
  {
    key: 'faq.hero_eyebrow',
    page: 'faq',
    kind: 'markdown',
    label: 'Hero eyebrow',
    default: 'FAQ',
  },
  {
    key: 'faq.hero_title',
    page: 'faq',
    kind: 'markdown',
    label: 'Hero title',
    default: 'Questions, plainly answered.',
  },
  {
    key: 'faq.hero_lede',
    page: 'faq',
    kind: 'markdown',
    label: 'Hero lede',
    default:
      "Five questions we hear most. If yours isn't here, write to us at hello@wwaave.com. We read every message.",
  },
  {
    key: 'how_it_works.hero_eyebrow',
    page: 'how_it_works',
    kind: 'markdown',
    label: 'Hero eyebrow',
    default: 'The flow',
  },
  {
    key: 'how_it_works.hero_title',
    page: 'how_it_works',
    kind: 'markdown',
    label: 'Hero title',
    default: 'How it works.',
  },
  {
    key: 'how_it_works.hero_lede',
    page: 'how_it_works',
    kind: 'markdown',
    label: 'Hero lede',
    default:
      'An exchange is mutual. The artist brings a performance. The venue offers a place to stay, a meal, and a kind audience. Here is how it goes from a search to a stay.',
  },
  {
    key: 'dispute_resolution.hero_eyebrow',
    page: 'dispute_resolution',
    kind: 'markdown',
    label: 'Hero eyebrow',
    default: 'Trust & safety',
  },
  {
    key: 'dispute_resolution.hero_title',
    page: 'dispute_resolution',
    kind: 'markdown',
    label: 'Hero title',
    default: 'Dispute resolution.',
  },
  {
    key: 'dispute_resolution.hero_lede',
    page: 'dispute_resolution',
    kind: 'markdown',
    label: 'Hero lede',
    default:
      "Most exchanges go well. When one doesn't, this is how we handle it: read by a human, decided by a human, summarised in writing.",
  },
  // Renamed 2026-08-18: the page is called "Our Ethos" now (Richie, feedback
  // y5uYAuKIbZM4aNSh323Lx). He had already set the title as a prod override, but
  // the REGISTRY defaults are what a fresh environment renders and what the
  // admin's "reset to default" restores, so they move too, or one click quietly
  // un-renames the page. The eyebrow could not simply follow the title: it read
  // "The WWAAVE Promise", which named the page by its retired name directly
  // above its new one, and pointed at a Promise section removed the same day.
  {
    key: 'what_we_are_and_arent.hero_eyebrow',
    page: 'what_we_are_and_arent',
    kind: 'markdown',
    label: 'Hero eyebrow',
    default: 'What we stand for',
  },
  {
    key: 'what_we_are_and_arent.hero_title',
    page: 'what_we_are_and_arent',
    kind: 'markdown',
    label: 'Hero title',
    default: 'Our Ethos',
  },
  {
    key: 'what_we_are_and_arent.hero_lede',
    page: 'what_we_are_and_arent',
    kind: 'markdown',
    label: 'Hero lede',
    default:
      'A community for travelling creatives and the venues that host them. The exchange is mutual: what you create for a place to stay, a meal, a kind audience.',
  },
  {
    key: 'community_guidelines.hero_eyebrow',
    page: 'community_guidelines',
    kind: 'markdown',
    label: 'Hero eyebrow',
    default: 'Community standards',
  },
  {
    key: 'community_guidelines.hero_title',
    page: 'community_guidelines',
    kind: 'markdown',
    label: 'Hero title',
    default: 'How we hold the line.',
  },
  {
    key: 'community_guidelines.hero_lede',
    page: 'community_guidelines',
    kind: 'markdown',
    label: 'Hero lede',
    default:
      'The community is the product. The platform is the scaffolding around the people in it, and these are the standards that keep the scaffolding worth standing on.',
  },
  {
    key: 'about.hero_eyebrow',
    page: 'about',
    kind: 'markdown',
    label: 'Hero eyebrow',
    default: "Our story · in Richie's words",
  },
  {
    key: 'about.hero_title',
    page: 'about',
    kind: 'markdown',
    label: 'Hero title',
    default: 'Run by a muso, not a tech bro.',
  },

  // ─── What we are and aren't — extended sections ───────────────────────
  {
    key: 'what_we_are_and_arent.definition',
    page: 'what_we_are_and_arent',
    kind: 'markdown',
    label: 'Definition body',
    helpText: 'The dictionary-style "wwaave /wāv/ n." heading is fixed; this is the definition text.',
    default:
      'Worldwide Artist and Venue Exchange. A global community connecting travelling creatives with host venues through mutual exchange. No cash. No fees. No middleman. Just a fair swap between people who have something to offer each other.',
  },
  // Retired 2026-08-18 with the rest of "The promise" section (see the eyebrow /
  // title slots below). ⚠️ These two ARE the WWAAVE Promise as written — the
  // "will never take a cut / never hold your money / never sell your data"
  // wording. Terms section 2 still links here calling this the page the full
  // promise lives on. Kept (append-only, prod overrides exist) so the copy is
  // recoverable the moment it needs a home again.
  {
    key: 'what_we_are_and_arent.promise_statement',
    page: 'what_we_are_and_arent',
    kind: 'markdown',
    label: 'Promise: headline statement',
    default:
      "WWAAVE doesn't take a cut of any payment between you and a venue. We never have. We never will.",
    deprecated: true,
  },
  {
    key: 'what_we_are_and_arent.promise_never',
    page: 'what_we_are_and_arent',
    kind: 'bullet_list',
    label: 'Promise: "WWAAVE will never" list',
    default: WHAT_WE_ARE_PROMISE_NEVER,
    deprecated: true,
  },
  {
    key: 'what_we_are_and_arent.model',
    page: 'what_we_are_and_arent',
    kind: 'bullet_list',
    label: 'The model (paragraphs)',
    helpText: 'One paragraph per entry; they render stacked.',
    default: WHAT_WE_ARE_MODEL,
  },
  {
    key: 'what_we_are_and_arent.commitment',
    page: 'what_we_are_and_arent',
    kind: 'bullet_list',
    label: 'The commitment (paragraphs)',
    helpText: 'One paragraph per entry; they render stacked.',
    default: WHAT_WE_ARE_COMMITMENT,
  },
  {
    key: 'what_we_are_and_arent.founder_note',
    page: 'what_we_are_and_arent',
    kind: 'markdown',
    label: "Founder's note body",
    default:
      "I learned the exchange WWOOFing my way across the world. Find a host, show up, work the land, get fed and welcomed, folded into a community. No cash, no middleman. Just an honest swap between people who need what the other has. It worked on farms. I couldn't see why it wouldn't work for creatives. This page is that idea, written down so you can hold me to it.",
  },

  // ─── What we are and aren't — section headings (eyebrow / title / lede) ─
  // Every section header on the page is CMS-editable so the whole page can be
  // reworded without a deploy (feedback 2026-07-03).
  {
    key: 'what_we_are_and_arent.definition_eyebrow',
    page: 'what_we_are_and_arent',
    kind: 'markdown',
    label: 'Definition: eyebrow',
    default: 'The definition',
  },
  {
    key: 'what_we_are_and_arent.definition_title',
    page: 'what_we_are_and_arent',
    kind: 'markdown',
    label: 'Definition: title',
    default: 'wwaave, defined.',
  },
  // Retired 2026-08-18: "The promise" section removed from the page. Richie had
  // overridden both of these to "REMOVE THIS SECTION" on prod, which is how the
  // request arrived (feedback y5uYAuKIbZM4aNSh323Lx). Slots kept (append-only)
  // but hidden from the CMS + no longer rendered.
  {
    key: 'what_we_are_and_arent.promise_eyebrow',
    page: 'what_we_are_and_arent',
    kind: 'markdown',
    label: 'Promise: eyebrow',
    default: 'The promise',
    deprecated: true,
  },
  {
    key: 'what_we_are_and_arent.promise_title',
    page: 'what_we_are_and_arent',
    kind: 'markdown',
    label: 'Promise: title',
    default: 'The WWAAVE Promise.',
    deprecated: true,
  },
  {
    key: 'what_we_are_and_arent.promise_lede',
    page: 'what_we_are_and_arent',
    kind: 'markdown',
    label: 'Promise: lede',
    default:
      "One sentence stretched into five, so there's no question what this platform will and won't do to you.",
    // Removed from the page at Richie's request — the section subheading was
    // dropped, so this slot no longer renders. Retired (not deleted) so any
    // future override doesn't orphan and it leaves the admin CMS editor.
    deprecated: true,
  },
  {
    // Retired 2026-07-11 (Richie: "REMOVE LINE"). The label above the "will never"
    // list no longer renders; slot kept (append-only) but hidden from the CMS.
    key: 'what_we_are_and_arent.promise_never_label',
    page: 'what_we_are_and_arent',
    kind: 'markdown',
    label: 'Promise: "will never" list label',
    default: 'WWAAVE will never:',
    deprecated: true,
  },
  // Retired 2026-08-18: "In contrast" section removed from the page. Richie had
  // overridden both of these to "REMOVE THIS SECTION" on prod (feedback
  // W87b8Y3XNcBtRBwqqw5k4). Slots kept (append-only) but hidden from the CMS +
  // no longer rendered.
  {
    key: 'what_we_are_and_arent.contrast_eyebrow',
    page: 'what_we_are_and_arent',
    kind: 'markdown',
    label: 'In contrast: eyebrow',
    default: 'In contrast',
    deprecated: true,
  },
  {
    key: 'what_we_are_and_arent.contrast_title',
    page: 'what_we_are_and_arent',
    kind: 'markdown',
    label: 'In contrast: title',
    default: "What it is, set against what it isn't.",
    deprecated: true,
  },
  {
    key: 'what_we_are_and_arent.contrast_lede',
    page: 'what_we_are_and_arent',
    kind: 'markdown',
    label: 'In contrast: lede',
    default:
      "Everything WWAAVE is, paired with everything we deliberately aren't, so the shape of the platform is unambiguous.",
    // Removed from the page at Richie's request — the section subheading was
    // dropped, so this slot no longer renders. Retired (not deleted) so any
    // future override doesn't orphan and it leaves the admin CMS editor.
    deprecated: true,
  },
  {
    key: 'what_we_are_and_arent.is_eyebrow',
    page: 'what_we_are_and_arent',
    kind: 'markdown',
    label: 'In contrast: "is" column eyebrow',
    default: 'What WWAAVE is',
    // The four column-header slots below were removed from the page at Richie's
    // request (2026-08-02) — the tick/cross icons and the card tints carry the
    // yes/no reading on their own. Retired, not deleted, so any override doesn't
    // orphan and they leave the admin CMS editor.
    deprecated: true,
  },
  {
    key: 'what_we_are_and_arent.is_title',
    page: 'what_we_are_and_arent',
    kind: 'markdown',
    label: 'In contrast: "is" column title',
    default: 'The yes column.',
    deprecated: true,
  },
  {
    key: 'what_we_are_and_arent.isnt_eyebrow',
    page: 'what_we_are_and_arent',
    kind: 'markdown',
    label: 'In contrast: "isn\'t" column eyebrow',
    default: "What WWAAVE isn't",
    deprecated: true,
  },
  {
    key: 'what_we_are_and_arent.isnt_title',
    page: 'what_we_are_and_arent',
    kind: 'markdown',
    label: 'In contrast: "isn\'t" column title',
    default: 'The no column.',
    deprecated: true,
  },
  {
    key: 'what_we_are_and_arent.model_eyebrow',
    page: 'what_we_are_and_arent',
    kind: 'markdown',
    label: 'The model: eyebrow',
    default: 'The model',
  },
  {
    key: 'what_we_are_and_arent.model_title',
    page: 'what_we_are_and_arent',
    kind: 'markdown',
    label: 'The model: title',
    default: 'How we make money.',
  },
  {
    key: 'what_we_are_and_arent.commitment_eyebrow',
    page: 'what_we_are_and_arent',
    kind: 'markdown',
    label: 'The commitment: eyebrow',
    default: 'The commitment',
  },
  {
    key: 'what_we_are_and_arent.commitment_title',
    page: 'what_we_are_and_arent',
    kind: 'markdown',
    label: 'The commitment: title',
    default: 'What we do with the money.',
  },
  {
    key: 'what_we_are_and_arent.commitment_lede',
    page: 'what_we_are_and_arent',
    kind: 'markdown',
    label: 'The commitment: lede',
    default:
      'WWAAVE is a social enterprise. The subscription pays for the platform; the rest funds the community that grew it.',
  },
  {
    key: 'what_we_are_and_arent.founder_eyebrow',
    page: 'what_we_are_and_arent',
    kind: 'markdown',
    label: "Founder's note: eyebrow",
    default: 'A note from Richie',
  },
  {
    key: 'what_we_are_and_arent.founder_title',
    page: 'what_we_are_and_arent',
    kind: 'markdown',
    label: "Founder's note: title",
    default: 'Why the promise reads the way it does.',
  },
  {
    key: 'what_we_are_and_arent.founder_signature',
    page: 'what_we_are_and_arent',
    kind: 'markdown',
    label: "Founder's note: signature",
    default: 'Richie',
  },

  // ─── Community guidelines — content ───────────────────────────────────
  {
    key: 'community_guidelines.lineage_quote',
    page: 'community_guidelines',
    kind: 'markdown',
    label: 'Lineage: pull quote',
    default:
      '“We borrowed this from WWOOF. They’ve kept a hospitality network honest for forty years.”',
  },
  {
    key: 'community_guidelines.lineage_body',
    page: 'community_guidelines',
    kind: 'markdown',
    label: 'Lineage: body',
    default:
      "WWOOF (Worldwide Opportunities on Organic Farms) has run a global host network on the exchange model since 1971. Their guardrails are the model we built on: verified members, mutual reviews, human-mediated disputes, a clear list of what counts as hospitality and what doesn't. The names are different here. The shape is the same.",
  },
  {
    key: 'community_guidelines.expectations',
    page: 'community_guidelines',
    kind: 'step_list',
    label: 'What we expect from everyone',
    helpText: "Each entry's title is bold (e.g. 'Warmth.'), body is the description.",
    default: CG_EXPECTATIONS,
  },
  {
    key: 'community_guidelines.refusals',
    page: 'community_guidelines',
    kind: 'step_list',
    label: "What we won't tolerate",
    helpText: "Each entry's title is bold, body is the description.",
    default: CG_REFUSALS,
  },
  {
    key: 'community_guidelines.guardrails',
    page: 'community_guidelines',
    kind: 'step_list',
    label: 'How we hold the line (mechanisms)',
    helpText: "Each entry's title is bold, body is the description.",
    default: CG_GUARDRAILS,
  },

  // ─── About — extended sections ────────────────────────────────────────
  {
    key: 'about.luang_prabang',
    page: 'about',
    kind: 'bullet_list',
    label: 'Luang Prabang story (paragraphs)',
    helpText: 'One paragraph per entry; they render stacked.',
    default: ABOUT_LUANG_PRABANG,
  },
  {
    key: 'about.why_exists',
    page: 'about',
    kind: 'bullet_list',
    label: 'Why this exists (paragraphs)',
    helpText: 'One paragraph per entry; they render stacked above the two join buttons.',
    default: ABOUT_WHY_EXISTS,
  },
  // Retired 2026-08-18: the bordered "From a small ripple…" card was removed
  // from the bottom of Our Story at Richie's request (feedback
  // tSkTYyeLO6oUl2W3v4kHM). Slots kept (append-only) but hidden from the CMS +
  // no longer rendered; prod holds overrides on both.
  {
    key: 'about.ripple_quote',
    page: 'about',
    kind: 'markdown',
    label: 'Ripple card: quote',
    default: 'From a small ripple, wwaaves are made.',
    deprecated: true,
  },
  {
    key: 'about.ripple_body',
    page: 'about',
    kind: 'markdown',
    label: 'Ripple card: body',
    default:
      "This isn't a platform a tech company built because it spotted a market. It's the one I needed. Every decision about how WWAAVE works, what we charge, what we don't, how we run disputes, what counts as a fair review, is made by people who have stood on the other side of the request. The artist and the host come first. The platform comes a distant second.",
    deprecated: true,
  },
  {
    // Retired 2026-07-10: the "Where to find us" contact section was removed
    // from the Our Story page (feedback K4eP); social links now live in the
    // site footer. Slot kept (append-only).
    key: 'about.contact_intro',
    page: 'about',
    kind: 'markdown',
    label: 'Get in touch: intro',
    default:
      'Three ways. Email is the surest one. The inbox is read by a human, and most messages get a reply within a working day.',
    deprecated: true,
  },
] as const;

// The full registry: the original base slots plus every per-page "extra" slot
// added to make previously-hardcoded public copy editable (see ./slots-extra).
export const CMS_SLOTS: ReadonlyArray<SlotDef> = [...BASE_SLOTS, ...EXTRA_SLOTS];

// ──────────────────────────────────────────────────────────────────────────────
// Helpers
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Image slot urls must be same-origin relative paths. This blocks an admin (or
 * a corrupt row) from baking an external / `javascript:` / protocol-relative
 * src into a public `<img>`, and keeps the same-origin Cloudflare Images
 * transform applicable. Allowed: `/img/...`, `/img/hero/x.avif`. Rejected:
 * `https://…`, `//evil`, `../../x`, anything not starting with a single `/`.
 */
function isSameOriginImagePath(url: string): boolean {
  if (!url.startsWith('/')) return false;
  if (url.startsWith('//')) return false; // protocol-relative
  if (url.includes('://')) return false;
  if (url.includes('..')) return false;
  return true;
}

/**
 * Validate + normalise a single image body. Shared by the `image` and
 * `image_list` slot kinds (the latter runs this per tile). Strips unknown keys
 * so the stored body_hash stays stable across saves.
 */
function validateOneImageBody(body: unknown): ImageBody {
  if (body === null || typeof body !== 'object' || Array.isArray(body)) {
    throw new Error('image slot expects an object body.');
  }
  const b = body as Record<string, unknown>;
  if (typeof b['url'] !== 'string' || b['url'].length === 0) {
    throw new Error('image slot requires a non-empty url.');
  }
  if (!isSameOriginImagePath(b['url'])) {
    throw new Error('image slot url must be a same-origin path beginning with "/".');
  }
  if (typeof b['alt'] !== 'string') {
    throw new Error('image slot alt must be a string.');
  }
  if (b['key'] !== undefined && typeof b['key'] !== 'string') {
    throw new Error('image slot key must be a string when present.');
  }
  if (b['width'] !== undefined && (typeof b['width'] !== 'number' || b['width'] <= 0)) {
    throw new Error('image slot width must be a positive number when present.');
  }
  if (b['height'] !== undefined && (typeof b['height'] !== 'number' || b['height'] <= 0)) {
    throw new Error('image slot height must be a positive number when present.');
  }
  if (
    b['opacity'] !== undefined &&
    (typeof b['opacity'] !== 'number' || b['opacity'] < 0 || b['opacity'] > 1)
  ) {
    throw new Error('image slot opacity must be a number between 0 and 1 when present.');
  }
  if (b['caption'] !== undefined && typeof b['caption'] !== 'string') {
    throw new Error('image slot caption must be a string when present.');
  }
  const out: ImageBody = { url: b['url'], alt: b['alt'] };
  if (typeof b['key'] === 'string') out.key = b['key'];
  if (typeof b['width'] === 'number') out.width = b['width'];
  if (typeof b['height'] === 'number') out.height = b['height'];
  if (typeof b['opacity'] === 'number') out.opacity = b['opacity'];
  // Trim then drop an empty caption so a cleared field doesn't persist as "".
  if (typeof b['caption'] === 'string' && b['caption'].trim().length > 0) {
    out.caption = b['caption'].trim();
  }
  return out;
}

const SLOT_BY_KEY = new Map<string, SlotDef>(CMS_SLOTS.map((s) => [s.key, s]));

export function getSlotDef(key: string): SlotDef | undefined {
  return SLOT_BY_KEY.get(key);
}

export function getSlotDefsForPage(page: PageId): SlotDef[] {
  return CMS_SLOTS.filter((s) => s.page === page && s.deprecated !== true);
}

export function isPageId(value: unknown): value is PageId {
  return typeof value === 'string' && (PAGE_IDS as readonly string[]).includes(value);
}

// Tab order: home first, then the six main public pages, then the remaining
// non-nav pages, then the two site-wide sets (Read on cards, site footer) last.
// This was "live-nav order" until 2026-08-18, when Our Ethos and Ripples came
// off the top nav; the order is left as-is deliberately — it is a stable
// editing order for the admin, not a mirror of the four remaining tabs.
export const PAGE_IDS: readonly PageId[] = [
  'home',
  'how_it_works',
  'what_we_are_and_arent',
  'founding_venue_offer',
  'notes_from_the_road',
  'about',
  'faq',
  'waitlist',
  'dispute_resolution',
  'community_guidelines',
  'privacy',
  'terms',
  'next_steps',
  'footer',
] as const;

/**
 * Validate that a parsed JSON body matches the declared kind. Returns the
 * normalised body on success, throws on shape mismatch.
 */
export function validateSlotBody(kind: SlotKind, body: unknown): SlotBody {
  if (kind === 'markdown') {
    if (typeof body !== 'string') throw new Error('markdown slot expects a string body.');
    return body;
  }
  if (kind === 'bullet_list') {
    if (!Array.isArray(body) || body.some((b) => typeof b !== 'string')) {
      throw new Error('bullet_list slot expects an array of strings.');
    }
    return body as string[];
  }
  if (kind === 'qa_list') {
    if (!Array.isArray(body)) throw new Error('qa_list slot expects an array.');
    // Normalise to {id, chip?, question, answer}, stripping unknown keys so
    // body_hash stays stable across saves (same discipline as the image branch).
    const normalised: QaItem[] = [];
    for (const item of body) {
      if (item === null || typeof item !== 'object') throw new Error('qa_list item not an object.');
      const i = item as Record<string, unknown>;
      if (typeof i['id'] !== 'string') throw new Error('qa_list item missing id.');
      if (typeof i['question'] !== 'string') throw new Error('qa_list item missing question.');
      if (!Array.isArray(i['answer']) || i['answer'].some((a) => typeof a !== 'string')) {
        throw new Error('qa_list item answer must be string[].');
      }
      // FR-041: `chip` was never validated — a non-string chip saved cleanly and
      // then crashed the whole public FAQ route at render (React: "Objects are
      // not valid as a React child"). Require string-or-absent, and drop it from
      // the normalised item when absent so a stray null/object can't ride through
      // into the KV snapshot or the injected head blob. Because this runs on the
      // READ path too, an already-stored bad-chip row now fails validation and is
      // skipped (falls back to default) instead of taking down the FAQ route.
      if (i['chip'] !== undefined && typeof i['chip'] !== 'string') {
        throw new Error('qa_list item chip must be a string when present.');
      }
      const q: QaItem = {
        id: i['id'],
        question: i['question'],
        answer: i['answer'] as string[],
      };
      if (typeof i['chip'] === 'string') q.chip = i['chip'];
      normalised.push(q);
    }
    // FR-101: reject duplicate ids as a second line of defence behind the
    // client's uuid minting. React keys and the public FAQ's per-item anchors
    // both assume uniqueness; a hand-typed Id or a legacy row saved under the
    // old length-derived scheme could still collide. Fail the save (and, on the
    // read path, fall back to default) rather than render a broken list.
    const seenIds = new Set<string>();
    for (const q of normalised) {
      if (seenIds.has(q.id)) throw new Error('qa_list item ids must be unique.');
      seenIds.add(q.id);
    }
    return normalised;
  }
  if (kind === 'image') {
    return validateOneImageBody(body);
  }
  if (kind === 'image_list') {
    if (!Array.isArray(body)) throw new Error('image_list slot expects an array of images.');
    // Validate each entry through the same rules as a single image slot, so the
    // same-origin guard + normalisation apply per tile. Length is capped on the
    // write path (enforceSlotBodyLimits) — the read path stays lenient so a
    // legitimately-long stored list is never silently dropped.
    return body.map((entry) => validateOneImageBody(entry));
  }
  // step_list
  if (!Array.isArray(body)) throw new Error('step_list slot expects an array.');
  for (const item of body) {
    if (item === null || typeof item !== 'object') throw new Error('step_list item not an object.');
    const i = item as Record<string, unknown>;
    if (typeof i['title'] !== 'string') throw new Error('step_list item missing title.');
    if (typeof i['body'] !== 'string') throw new Error('step_list item missing body.');
    if (i['footnote'] !== undefined && typeof i['footnote'] !== 'string') {
      throw new Error('step_list item footnote must be string when present.');
    }
  }
  return body as StepItem[];
}

// FR-042: write-time size caps. Kept SEPARATE from validateSlotBody because that
// function also runs on the READ path (buildSnapshotFromD1, getOverridesForPage);
// enforcing size there would silently drop any legitimately-large existing
// override — the long-form legal-page markdown bodies most of all — from the
// public snapshot. These are generous robustness backstops against a pathological
// save (a megabyte paste, a runaway list edit) that would otherwise inflate every
// load of that route, the KV snapshot, the injected <head> blob, and double up in
// the audit log — NOT tight editorial limits. Call from the write path only,
// after validateSlotBody. Throws a plain-English message on violation.
const MAX_MARKDOWN_CHARS = 50_000;
const MAX_LIST_ITEMS = 200;
const MAX_LIST_ITEM_CHARS = 5_000;
const MAX_QA_QUESTION_CHARS = 1_000;
const MAX_QA_ANSWER_PARAS = 50;
const MAX_QA_CHIP_CHARS = 120;
const MAX_STEP_TITLE_CHARS = 500;
const MAX_STEP_FOOTNOTE_CHARS = 2_000;
const MAX_IMAGE_ALT_CHARS = 500;
// Marquee tile captions are a short label, not a paragraph — keep them tight so
// they fit over a 16:9 tile without wrapping into a wall of text.
export const MAX_IMAGE_CAPTION_CHARS = 140;
// The "Who's on wwaave" marquee holds up to this many tiles per row. The gallery
// editor enforces the same ceiling client-side; this is the server backstop.
export const MAX_IMAGE_LIST_ITEMS = 40;

export function enforceSlotBodyLimits(kind: SlotKind, body: SlotBody): void {
  if (kind === 'markdown') {
    if ((body as string).length > MAX_MARKDOWN_CHARS) {
      throw new Error(`This is too long to save (max ${MAX_MARKDOWN_CHARS} characters).`);
    }
    return;
  }
  if (kind === 'bullet_list') {
    const list = body as string[];
    if (list.length > MAX_LIST_ITEMS) throw new Error(`Too many items (max ${MAX_LIST_ITEMS}).`);
    if (list.some((b) => b.length > MAX_LIST_ITEM_CHARS)) {
      throw new Error(`An item is too long (max ${MAX_LIST_ITEM_CHARS} characters).`);
    }
    return;
  }
  if (kind === 'qa_list') {
    const items = body as QaItem[];
    if (items.length > MAX_LIST_ITEMS) throw new Error(`Too many questions (max ${MAX_LIST_ITEMS}).`);
    for (const q of items) {
      if (q.question.length > MAX_QA_QUESTION_CHARS) {
        throw new Error(`A question is too long (max ${MAX_QA_QUESTION_CHARS} characters).`);
      }
      if (q.answer.length > MAX_QA_ANSWER_PARAS) {
        throw new Error(`An answer has too many paragraphs (max ${MAX_QA_ANSWER_PARAS}).`);
      }
      if (q.answer.some((a) => a.length > MAX_LIST_ITEM_CHARS)) {
        throw new Error(`An answer paragraph is too long (max ${MAX_LIST_ITEM_CHARS} characters).`);
      }
      if (q.chip !== undefined && q.chip.length > MAX_QA_CHIP_CHARS) {
        throw new Error(`A chip label is too long (max ${MAX_QA_CHIP_CHARS} characters).`);
      }
    }
    return;
  }
  if (kind === 'image') {
    if ((body as ImageBody).alt.length > MAX_IMAGE_ALT_CHARS) {
      throw new Error(`Alt text is too long (max ${MAX_IMAGE_ALT_CHARS} characters).`);
    }
    return;
  }
  if (kind === 'image_list') {
    const images = body as ImageListBody;
    if (images.length > MAX_IMAGE_LIST_ITEMS) {
      throw new Error(`Too many images (max ${MAX_IMAGE_LIST_ITEMS}).`);
    }
    for (const img of images) {
      if (img.alt.length > MAX_IMAGE_ALT_CHARS) {
        throw new Error(`Alt text is too long (max ${MAX_IMAGE_ALT_CHARS} characters).`);
      }
      if (img.caption !== undefined && img.caption.length > MAX_IMAGE_CAPTION_CHARS) {
        throw new Error(`A caption is too long (max ${MAX_IMAGE_CAPTION_CHARS} characters).`);
      }
    }
    return;
  }
  // step_list
  const steps = body as StepItem[];
  if (steps.length > MAX_LIST_ITEMS) throw new Error(`Too many steps (max ${MAX_LIST_ITEMS}).`);
  for (const s of steps) {
    if (s.title.length > MAX_STEP_TITLE_CHARS) {
      throw new Error(`A step title is too long (max ${MAX_STEP_TITLE_CHARS} characters).`);
    }
    if (s.body.length > MAX_LIST_ITEM_CHARS) {
      throw new Error(`A step body is too long (max ${MAX_LIST_ITEM_CHARS} characters).`);
    }
    if (s.footnote !== undefined && s.footnote.length > MAX_STEP_FOOTNOTE_CHARS) {
      throw new Error(`A step footnote is too long (max ${MAX_STEP_FOOTNOTE_CHARS} characters).`);
    }
  }
}
